<script setup lang="ts">
/**
 * HARGA — tema v3.
 *
 * Dua produk dengan dua cara berhitung yang berbeda, dan perbedaan itu
 * dijelaskan, bukan disembunyikan: BerkasKilat dijual per masa aktif karena
 * menyiapkan berkas jarang selesai sekali percobaan, RapiKontak dijual per
 * kredit karena tiap proses berdiri sendiri. Pembeli yang mengerti dasar
 * hitungannya tidak merasa dijebak saat kreditnya habis.
 *
 * Halaman ini tidak memanggil backend sama sekali.
 */


const faq = [
  {
    label: 'Kenapa BerkasKilat dijual per hari, bukan per proses?',
    content:
      'Karena menyiapkan berkas pendaftaran jarang selesai sekali percobaan. Pengumuman instansi kadang direvisi di tengah masa pendaftaran, dan Anda mungkin perlu mengulang dengan ukuran berbeda. Kalau dihitung per proses, Anda akan ragu mencoba lagi — padahal justru mencoba lagi itu yang Anda butuhkan.'
  },
  {
    label: 'Satu kredit RapiKontak itu berapa nomor?',
    content:
      'Satu kredit dipakai untuk satu kali proses, dan satu proses menampung sampai 10.000 nomor. Jadi kalau daftar nomor Anda 8.000 baris, itu tetap satu kredit.'
  },
  {
    label: 'Ada langganan atau perpanjangan otomatis?',
    content:
      'Tidak ada. Tidak ada kartu yang tersimpan, tidak ada tagihan bulanan, dan tidak ada yang berjalan diam-diam. Setiap pembelian berdiri sendiri.'
  },
  {
    label: 'Kapan masa aktif tiga hari mulai dihitung?',
    content:
      'Sejak kode aktivasi pertama kali ditukarkan, bukan sejak pembayaran diterima. Kalau Anda membayar hari ini tetapi baru menukarkan kodenya seminggu lagi, masa aktif Anda tetap penuh tiga hari.'
  },
  {
    label: 'Bisa minta uang kembali?',
    content:
      'Ketentuan pengembalian dana kami tulis terpisah di halaman Ketentuan Refund. Bacalah sebelum membayar, bukan sesudah.'
  }
]

useGerak()

/* SEO dibawa UTUH dari versi lama halaman ini (kini di /legacy/harga). */
const situs = useSitusUrl()

pakaiSeo({
  judul: 'Harga — BerkasKilat & RapiKontak | CoreAsia Beres',
  deskripsi:
    'Daftar harga tools.coreasia.id: BerkasKilat Rp15.000 untuk akses 3 hari tanpa batas proses, dan RapiKontak mulai Rp10.000 per kredit proses. Alat kompres tetap gratis.',
  path: '/harga',
  jsonLd: [
    jsonLdFaq(faq.map((f) => ({ tanya: f.label, jawab: f.content }))),
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['Harga', '/harga']
    ])
  ]
})
</script>

<template>
  <div>
    <!-- ══ HERO ═════════════════════════════════════════════════════════════ -->
    <section class="relative overflow-hidden">
      <div class="ca-aura" aria-hidden="true" />

      <div class="relative mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 pt-10 sm:pt-14 pb-10">
        <div class="max-w-2xl">
          <CaBadge tone="emas-lembut" icon="i-lucide-wallet">Sekali bayar, tanpa langganan</CaBadge>

          <h1 class="ca-display mt-5 text-ink text-balance">Harga apa adanya</h1>

          <p class="mt-5 text-lg text-muted leading-relaxed">
            Bayar lewat QRIS dari e-wallet atau m-banking mana pun. Tidak ada perpanjangan
            otomatis, tidak ada biaya tersembunyi, dan tidak perlu membuat akun.
          </p>
        </div>
      </div>
    </section>

    <!-- ══ BERKASKILAT ══════════════════════════════════════════════════════
         Dipisah per produk, bukan tiga kartu berjajar tanpa konteks: dasar
         hitungannya berbeda, dan menyandingkan "3 hari" dengan "1 kredit"
         dalam satu baris membuat orang membandingkan angka yang tidak
         sebanding. -->
    <section class="py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Berbasis waktu"
          badge-tone="emas"
          badge-ikon="i-lucide-folder-check"
          judul="BerkasKilat"
          ringkas="Seluruh berkas pendaftaran disesuaikan dengan ketentuan resmi instansi tujuan. Satu harga, proses tanpa batas selama masa aktif."
        />

        <div data-muncul class="mt-8 grid gap-5 lg:grid-cols-2">
          <CaPriceCard
            nama="Akses 3 hari"
            ringkas="Proses berkali-kali selama tiga hari, tanpa hitungan per proses."
            :harga="15000"
            satuan="/ akses 3 hari"
            :fitur="[
              'Semua preset instansi yang tersedia',
              'Unggah banyak berkas dalam satu proses',
              'Hasil ZIP berisi berkas, checklist, dan laporan per berkas',
              'Masa aktif mulai saat kode ditukarkan',
              'Boleh diulang sepuasnya selama masa aktif'
            ]"
            cta-label="Beli via QRIS — Rp15.000"
            cta-to="/checkout?sku=bk-3d"
            sorot
            badge="Paling dipilih"
          />

          <CaCard tone="soft" class="flex flex-col">
            <p class="ca-h3 text-ink">Untuk siapa paket ini</p>
            <ul class="mt-4 flex flex-col gap-3 flex-1">
              <li
                v-for="t in [
                  'Pendaftar CPNS, SNBT, atau SPMB yang berkasnya lebih dari satu',
                  'Siapa pun yang pengumuman instansinya masih mungkin direvisi',
                  'Yang sudah pernah ditolak karena ukuran berkas dan tidak mau mengulang lagi'
                ]"
                :key="t"
                class="flex gap-2.5 text-[0.9375rem] leading-relaxed text-ink"
              >
                <UIcon name="i-lucide-check" class="size-5 shrink-0 text-ok mt-0.5" aria-hidden="true" />
                <span>{{ t }}</span>
              </li>
            </ul>
            <p class="ca-caption text-muted mt-5 leading-relaxed">
              Kalau berkas Anda hanya satu, tidak ada alasan membayar — pakai alat kompres gratis
              di bawah.
            </p>
            <CaButton variant="secondary" size="lg" block class="mt-4" to="/berkaskilat">
              Baca dulu cara kerjanya
            </CaButton>
          </CaCard>
        </div>
      </div>
    </section>

    <!-- ══ RAPIKONTAK ═══════════════════════════════════════════════════════ -->
    <section id="rapikontak" class="ca-tunda scroll-mt-20 py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Berbasis kredit"
          badge-ikon="i-lucide-contact"
          judul="RapiKontak"
          ringkas="Daftar nomor WhatsApp yang berantakan jadi format 62 yang seragam, tanpa duplikat, siap diimpor. Satu kredit untuk satu kali proses hingga 10.000 nomor."
        />

        <div data-muncul class="mt-8 grid gap-5 lg:grid-cols-2">
          <CaPriceCard
            nama="Sekali Pakai"
            ringkas="Untuk mencoba dulu, atau untuk satu daftar nomor saja."
            :harga="10000"
            satuan="/ 1 kredit"
            :fitur="[
              'Hingga 10.000 nomor per proses',
              'Normalisasi 08xx menjadi 62xx otomatis',
              'Duplikat dan nomor tidak valid dibuang',
              'Ekspor CSV + VCF',
              'Berkas nomor tidak valid terpisah'
            ]"
            cta-label="Beli via QRIS — Rp10.000"
            cta-to="/checkout?sku=rk-1"
          />

          <CaPriceCard
            nama="Hemat 5x"
            ringkas="Untuk yang memang rutin merapikan daftar nomor."
            :harga="40000"
            satuan="/ 5 kredit"
            :fitur="[
              'Semua yang ada di paket Sekali Pakai',
              '5 kredit proses, dipakai kapan saja',
              'Hemat Rp10.000 dibanding beli satuan',
              'Kredit tidak hangus per bulan'
            ]"
            cta-label="Beli via QRIS — Rp40.000"
            cta-to="/checkout?sku=rk-5"
            sorot
            badge="Paling hemat"
          />
        </div>
      </div>
    </section>

    <!-- ══ KOMPRES BANYAK ═══════════════════════════════════════════════════
         Dibedakan tegas dari alat kompres GRATIS (yang tetap 1 berkas) dan dari
         BerkasKilat (yang menjual preset instansi). Ini kompres massal generik:
         banyak berkas sekaligus jadi satu ZIP. -->
    <section id="kompres-banyak" class="ca-tunda scroll-mt-20 py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Berbasis kredit"
          badge-ikon="i-lucide-layers"
          judul="Kompres Banyak"
          ringkas="Alat kompres tetap gratis untuk 1 berkas. Kalau berkasnya banyak — puluhan foto atau PDF sekaligus — kompres semuanya dalam satu proses dan unduh jadi satu ZIP. Satu kredit untuk satu berkas."
        />

        <div data-muncul class="mt-8 grid gap-5 lg:grid-cols-2">
          <CaPriceCard
            nama="Paket 30 berkas"
            ringkas="Sekali beli, borongan satu kali. Kredit tidak hangus per bulan."
            :harga="5000"
            satuan="/ 30 berkas"
            :fitur="[
              'Kompres sampai 30 berkas dalam antrean sekaligus',
              'Foto (JPG/PNG/WebP) maupun PDF',
              'Satu target ukuran untuk seluruh batch',
              'Hasil dibungkus jadi satu file ZIP',
              '1 kredit = 1 berkas, dipakai kapan saja'
            ]"
            cta-label="Beli via QRIS — Rp5.000"
            cta-to="/checkout?sku=kb-30"
            sorot
            badge="Paling sederhana"
          />

          <CaCard tone="soft" class="flex flex-col">
            <p class="ca-h3 text-ink">Bedanya dengan yang lain</p>
            <ul class="mt-4 flex flex-col gap-3 flex-1">
              <li
                v-for="t in [
                  'Alat kompres GRATIS: tetap 1 berkas per proses — tidak berubah, tidak perlu bayar.',
                  'Kompres Banyak: banyak berkas generik sekaligus jadi 1 ZIP, tanpa preset instansi.',
                  'BerkasKilat: khusus berkas pendaftaran dengan preset ukuran per instansi + laporan.'
                ]"
                :key="t"
                class="flex gap-2.5 text-[0.9375rem] leading-relaxed text-ink"
              >
                <UIcon name="i-lucide-check" class="size-5 shrink-0 text-ok mt-0.5" aria-hidden="true" />
                <span>{{ t }}</span>
              </li>
            </ul>
            <p class="ca-caption text-muted mt-5 leading-relaxed">
              Kalau berkas Anda cuma satu, tidak ada alasan membayar — pakai alat kompres gratis di
              bawah.
            </p>
            <CaButton variant="secondary" size="lg" block class="mt-4" to="/kompres-foto">
              Coba kompres gratis dulu
            </CaButton>
          </CaCard>
        </div>
      </div>
    </section>

    <!-- ══ EBOOK ════════════════════════════════════════════════════════════
         Bukan alat, jadi bentuk kartunya pun berbeda: tidak ada kredit dan
         tidak ada masa aktif, yang dibeli adalah berkasnya. Ditaruh setelah
         alat berbayar karena ia produk pelengkap, bukan pengganti. -->
    <section id="buku" class="ca-tunda scroll-mt-20 py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Sekali beli"
          badge-ikon="i-lucide-book-open"
          judul="Ebook Melek Komputer"
          ringkas="Panduan 95 halaman untuk pemula total: komputer dasar, 90+ rumus Excel dengan contoh kasus nyata, dan logika di balik semua program. Latihan tiap bab lengkap dengan kunci jawaban."
        />

        <div data-muncul class="mt-8 grid gap-5 lg:grid-cols-[220px_1fr] lg:items-start">
          <NuxtLink to="/buku/melek-komputer" class="block">
            <img
              src="/buku/sampul.jpg"
              alt="Sampul ebook Melek Komputer"
              width="1075"
              height="1521"
              loading="lazy"
              class="w-full rounded-xl border border-line shadow-lg"
            />
          </NuxtLink>

          <CaCard tone="soft" class="flex flex-col">
            <p class="ca-h3 text-ink">Rp20.000 — harga perkenalan</p>
            <ul class="mt-4 flex flex-col gap-3 flex-1">
              <li
                v-for="t in [
                  'PDF 95 halaman, 26 bab dan 4 lampiran, langsung diunduh setelah bayar.',
                  'Berkasnya diterbitkan atas nama Anda dan memuat kode lisensi di tiap halaman.',
                  'Pembaruan edisi berikutnya gratis untuk pemilik lisensi.',
                  'Bisa dibaca di HP, tablet, maupun laptop, dan boleh dicetak untuk sendiri.'
                ]"
                :key="t"
                class="flex gap-2.5 text-[0.9375rem] leading-relaxed text-ink"
              >
                <UIcon name="i-lucide-check" class="size-5 shrink-0 text-ok mt-0.5" aria-hidden="true" />
                <span>{{ t }}</span>
              </li>
            </ul>
            <p class="ca-caption text-muted mt-5 leading-relaxed">
              Belum yakin? Dua belas halaman pertama bisa dibaca gratis lebih dulu.
            </p>
            <CaButton size="lg" block class="mt-4" to="/buku/melek-komputer">
              Lihat isi bukunya
            </CaButton>
          </CaCard>
        </div>
      </div>
    </section>

    <!-- ══ YANG TETAP GRATIS ════════════════════════════════════════════════
         Ditaruh SETELAH harga dengan sengaja. Menaruhnya lebih dulu akan
         membuat sebagian orang berhenti membaca di situ — padahal sebagian
         dari mereka memang butuh yang berbayar. Ditaruh di sini, ia berfungsi
         sebagai bukti kejujuran, bukan sebagai jalan keluar. -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Gratis selamanya"
          badge-ikon="i-lucide-gift"
          judul="Yang tidak perlu Anda bayar"
          ringkas="Kompres foto dan PDF sepuasnya, tanpa akun dan tanpa batas pemakaian. Gratis untuk siapa saja, dan akan tetap begitu."
        />

        <div class="ca-runtun mt-8 grid gap-4 sm:grid-cols-2">
          <CaCard
            v-for="g in [
              {
                ikon: 'i-lucide-image',
                nama: 'Kompres foto',
                detail: 'Pas foto atau scan KTP diperkecil sampai persis di bawah target KB, tanpa jadi pecah.',
                ke: '/kompres-foto'
              },
              {
                ikon: 'i-lucide-file-text',
                nama: 'Kompres PDF',
                detail: 'Ijazah atau transkrip hasil scan diperkecil sampai lolos batas unggah instansi.',
                ke: '/kompres-pdf'
              }
            ]"
            :key="g.nama"
            data-muncul
            class="flex flex-col"
          >
            <div class="flex items-start gap-3.5">
              <span
                class="ca-ikon-emas ca-lapis-3d grid place-items-center size-11 shrink-0 rounded-control"
                aria-hidden="true"
              >
                <UIcon :name="g.ikon" class="size-5" />
              </span>
              <div class="min-w-0">
                <p class="ca-h3 text-ink">{{ g.nama }}</p>
                <p class="ca-caption text-muted mt-0.5">Tanpa akun, tanpa batas pemakaian</p>
              </div>
            </div>
            <p class="mt-4 text-[0.9375rem] leading-relaxed text-muted flex-1">{{ g.detail }}</p>
            <CaButton variant="secondary" size="lg" block class="mt-5" :to="g.ke">
              Buka alatnya
            </CaButton>
          </CaCard>
        </div>

        <p class="mt-6 text-sm text-muted leading-relaxed max-w-2xl">
          RapiKontak dan BerkasKilat menangani pekerjaan yang lebih besar — merapikan ribuan nomor
          atau seluruh berkas pendaftaran sekaligus — dan tersedia sebagai paket sekali bayar yang
          ringan di kantong.
        </p>
      </div>
    </section>

    <!-- ══ FAQ PEMBAYARAN ═══════════════════════════════════════════════════ -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Sebelum Anda membayar"
          badge-ikon="i-lucide-message-circle-question"
          judul="Pertanyaan soal harga dan pembayaran"
        />
        <div data-muncul class="mt-8 max-w-3xl">
          <CaFaqAccordion :item="faq" gerak />
        </div>

        <div class="mt-8 flex flex-col sm:flex-row gap-3">
          <CaButton variant="secondary" size="lg" to="/redeem">
            Sudah punya kode — tukar sekarang
          </CaButton>
          <CaButton variant="ghost" size="lg" to="/refund">Baca ketentuan refund</CaButton>
        </div>
      </div>
    </section>
  </div>
</template>
